import type { Edge, Node } from 'reactflow';

/**
 * Arruma o desenho do fluxo em camadas da esquerda para a direita.
 *
 * Não é enfeite: o editor só sabia criar blocos em posição aleatória, então
 * um fluxo de dez passos vira uma pilha de cartões sobrepostos em que não dá
 * para ver nem seguir uma aresta. Um clique resolve.
 *
 * O algoritmo é de propósito o mais simples que funciona:
 *  - a camada de um nó é a maior distância até ele a partir de uma entrada
 *    (nó sem aresta chegando), para que uma aresta nunca aponte para trás;
 *  - ciclos (fluxo que volta para um passo anterior) são legítimos aqui, e
 *    param na primeira revisita — sem isso o laço nunca terminaria;
 *  - dentro da camada, os nós ficam na ordem em que os pais aparecem, o que
 *    mantém "Sim" em cima de "Não".
 */

const LARGURA_DA_CAMADA = 320;
const ALTURA_DA_LINHA = 190;

export function organizarFluxo(nos: Node[], arestas: Edge[]): Node[] {
  if (nos.length === 0) return nos;

  const saidas = new Map<string, string[]>();
  const grauDeEntrada = new Map<string, number>();
  for (const n of nos) {
    saidas.set(n.id, []);
    grauDeEntrada.set(n.id, 0);
  }
  for (const a of arestas) {
    if (!saidas.has(a.source) || !grauDeEntrada.has(a.target)) continue;
    saidas.get(a.source)!.push(a.target);
    grauDeEntrada.set(a.target, grauDeEntrada.get(a.target)! + 1);
  }

  // Entradas do fluxo. Se tudo tem entrada (fluxo todo em ciclo), começa pelo
  // primeiro nó para não devolver o desenho intacto.
  const entradas = nos.filter((n) => grauDeEntrada.get(n.id) === 0).map((n) => n.id);
  const raizes = entradas.length > 0 ? entradas : [nos[0]!.id];

  const camada = new Map<string, number>();
  const fila: Array<{ id: string; nivel: number }> = raizes.map((id) => ({ id, nivel: 0 }));
  const visitasPorNo = new Map<string, number>();

  while (fila.length > 0) {
    const { id, nivel } = fila.shift()!;
    const vistas = visitasPorNo.get(id) ?? 0;
    // Um nó com N pais é revisitado N vezes para que a camada seja a maior
    // distância. O teto corta ciclos sem precisar detectá-los.
    if (vistas > nos.length) continue;
    visitasPorNo.set(id, vistas + 1);

    const atual = camada.get(id);
    if (atual !== undefined && atual >= nivel) continue;
    camada.set(id, nivel);
    for (const alvo of saidas.get(id) ?? []) fila.push({ id: alvo, nivel: nivel + 1 });
  }

  // Nós soltos (sem nenhuma aresta) vão para uma camada própria no fim, em
  // vez de empilhar na origem.
  const camadaSolta = Math.max(0, ...camada.values()) + 1;
  for (const n of nos) if (!camada.has(n.id)) camada.set(n.id, camadaSolta);

  const porCamada = new Map<number, string[]>();
  for (const n of nos) {
    const c = camada.get(n.id)!;
    if (!porCamada.has(c)) porCamada.set(c, []);
    porCamada.get(c)!.push(n.id);
  }

  // Cada camada é centrada no mesmo eixo, para que um caminho que se abre em
  // dois ramos fique simétrico em volta do tronco.
  const maiorCamada = Math.max(...Array.from(porCamada.values(), (ids) => ids.length));
  const eixoY = 60 + ((maiorCamada - 1) * ALTURA_DA_LINHA) / 2;

  const posicao = new Map<string, { x: number; y: number }>();
  for (const [c, ids] of porCamada) {
    const alturaDaCamada = (ids.length - 1) * ALTURA_DA_LINHA;
    ids.forEach((id, i) => {
      posicao.set(id, {
        x: 60 + c * LARGURA_DA_CAMADA,
        y: eixoY - alturaDaCamada / 2 + i * ALTURA_DA_LINHA,
      });
    });
  }

  return nos.map((n) => ({ ...n, position: posicao.get(n.id) ?? n.position }));
}
